/**
 * @file planNoticeCopy.ts
 * @brief The words of the plan-change dialog, from our real plan rules only
 *        (the catalogue the server sends), never guessed. No em dashes, no
 *        competitor names (plans-visible brief C, 21-10-2026).
 *
 * @author DES
 * @date 21-10-2026
 */

import type { TipKey } from "./planTips.ts";

type Num = number | null | undefined;
export type NoticeCatalogRow = {
  code: string;
  name: string;
  includedMinutes?: Num;
  notesMinutes?: Num;
  askLimit?: Num;
  historyDays?: Num;
};
export type NoticeFree =
  | { notesMinutes?: Num; askLimit?: Num; historyDays?: Num; notAvailable?: { code: string } | null }
  | null
  | undefined;

export type PlanNoticeCopy = {
  title: string;
  /** The plan's name, for the "Welcome to …" pill and the title. */
  planName: string;
  /** Heading over `can`: "You can now" on a paid plan, else "You can still". */
  canHeading: string;
  can: string[];
  cannot: string[];
  /**
   * The connected-apps lines under the divider (MCP, Asana), worded for who
   * can really use them today (planTips FEATURE_ROWS). Empty for a failed
   * payment.
   */
  apps: string[];
  /** Line → its (i) tip (planTips.ts); lines without one have none. */
  tips: Record<string, TipKey>;
};

const n = (v: Num) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const fmt = (v: number) => v.toLocaleString("en-GB");

/**
 * @param to The plan code now in force, or "past_due" when a payment failed.
 * @param free The Free tier's terms (`freePlan`), when the server sends them.
 */
export function planNoticeCopy(
  to: string,
  {
    catalog,
    free,
    audience = "personal",
  }: { catalog: readonly NoticeCatalogRow[]; free?: NoticeFree; audience?: "personal" | "company_admin" | "company_member" },
): PlanNoticeCopy {
  const code = to.toLowerCase();
  // MCP: personal accounts always; a company once an admin turns on AI
  // connectors (opt-in). Task apps (Asana today, more to come, so the
  // words stay generic): every account (DES 21-10-2026).
  const mcpLine =
    audience === "personal"
      ? "Let Claude, ChatGPT and other AI assistants read your meetings"
      : "Let Claude, ChatGPT and other AI assistants read your meetings, once an admin turns it on";
  const tasksLine = "Send meeting to-dos to your task apps, like Asana";
  const apps = [mcpLine, tasksLine];
  const appTips: Record<string, TipKey> = { [mcpLine]: "mcp", [tasksLine]: "taskApps" };
  if (code === "past_due") {
    return {
      title: "The last payment didn't go through",
      planName: "",
      canHeading: "You can still",
      can: ["Keep using pay as you go", "Open every meeting and note you have"],
      cannot: ["Use the plan's included minutes until the card is updated"],
      apps: [],
      tips: { "Keep using pay as you go": "extra", "Use the plan's included minutes until the card is updated": "intel" },
    };
  }
  if (code === "free" || code === "basic" || code === "none") {
    const notes = n(free?.notesMinutes) ?? 600;
    const ask = n(free?.askLimit) ?? 10;
    const days = n(free?.historyDays) ?? 30;
    const notesLine = `Record Meeting Notes, ${fmt(notes)} minutes a month`;
    const askLine = `${fmt(ask)} Ask AI questions a month`;
    const paygLine = "Use full Intelligence and interpretation, paid per minute";
    const historyLine = `See meetings and notes older than ${fmt(days)} days`;
    // Blocked country (B1): no free minutes and no pay as you go, never "0 minutes a month".
    if (free?.notAvailable) {
      const blockedLine = "Record or pay per minute from your country";
      return {
        title: "You're now on Free",
        planName: "Free",
        canHeading: "You can still",
        can: [askLine],
        cannot: [blockedLine, historyLine],
        apps,
        tips: { [askLine]: "ask", [historyLine]: "history", ...appTips },
      };
    }
    return {
      title: "You're now on Free",
      planName: "Free",
      canHeading: "You can still",
      can: [notesLine, askLine, paygLine],
      cannot: [historyLine],
      apps,
      tips: { [notesLine]: "notes", [askLine]: "ask", [paygLine]: "extra", [historyLine]: "history", ...appTips },
    };
  }
  const row = catalog.find((p) => p.code === code);
  const name = row?.name ?? (code === "business" ? "Business" : code === "pro" ? "Pro" : code);
  const can: string[] = [];
  const tips: Record<string, TipKey> = { ...appTips };
  const add = (line: string, tip: TipKey) => {
    can.push(line);
    tips[line] = tip;
  };
  const intel = n(row?.includedMinutes);
  if (intel != null) {
    if (code === "business") add(`Use ${fmt(intel)} Intelligence minutes per seat a month, shared by the team`, "pooled");
    else add(`Use ${fmt(intel)} Intelligence minutes a month`, "intel");
  }
  add(n(row?.notesMinutes) == null ? "Take Meeting Notes with no monthly cap" : `Take Meeting Notes, ${fmt(n(row?.notesMinutes)!)} minutes a month`, "notesPlan");
  if (code === "business") add("Give every member of your company a seat", "seats");
  else add(n(row?.historyDays) == null ? "See your full meeting history" : `See meetings from the last ${fmt(n(row?.historyDays)!)} days`, "history");
  return { title: `You're now on ${name}`, planName: name, canHeading: "You can now", can: can.slice(0, 3), cannot: [], apps, tips };
}
